import {
  CONSUMER_CMS_SHOPPING_CART,
  errorMock,
  handleTimer,
  IGlobalConfigCMS,
  IPersonalInfoCmsMobile,
  IProduct,
  useCmsConfig,
  useGenerateOTP,
  useGetFindCaller,
  useVerifyOTP,
} from '@vfit/consumer/data-access';
import { useEnergyCheckout } from '../../../energy.context';
import { ICheckoutData } from '../../checkoutEnergy.models';
import TaggingOtpCard from './otpCard.tagging';
import { getTrackError, organizeOtp, Otp } from '@vfit/consumer/components';
import { API, errorManager, ErrorService, resetData, trackView } from '@vfit/shared/data-access';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { getEncryptedMsisdn, getEnergyTrackError } from '../../checkoutEnergy.utils';

const OtpCard = ({ handleOnFinish }: ICheckoutData) => {
  const {
    findCaller,
    product,
    owningData,
    timerID,
    setFindCaller,
    setIsDisabledBackButton,
    setTimerID,
  } = useEnergyCheckout();
  const queryClient = useQueryClient();
  const { mutate: generateOtp, isError: isErrorGenerateOtp, error: generateOtpError } = useGenerateOTP();
  const {
    mutate: mutateVerifyOtp,
    data: dataVerifyOtp,
    isSuccess: isSuccessVerifyOtp,
    isError: isErrorVerifyOtp,
    error: verifyOtpErrorData,
  } = useVerifyOTP();
  const {
    data: findcallerPostOtpData,
    error: findcallerPostOtpError,
    isError: isErrorFindcallerPostOtp,
    isSuccess: isSuccessFindcallerPostOtp,
    refetch: refetchFindcallerPostOtp,
  } = useGetFindCaller(
    { fiscalCode: owningData?.owningIndividual?.fiscalCode?.toUpperCase() },
    {
      enabled: false,
    }
  );

  const dataFromCms = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PERSONAL_DATA_ENERGY
  ) as IPersonalInfoCmsMobile;

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const handleSendOtp = (field: string, fieldType: string) => {
    resetData(queryClient, ['generateOTP']);
    generateOtp({
      identifier: field,
      identifierType: fieldType,
    });
    if (
      dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount &&
      !isNaN(parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount)) &&
      parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount) > 0
    ) {
      clearInterval(timerID);
      setIsTimerActive(true);
      const timer = handleTimer(
        parseInt(dataFromCms?.personalinfomobile?.clientRecognition?.timer?.amount),
        () => setIsTimerActive(false)
      );
      setTimerID(timer);
    }
  };

  const handleVerify = (otp: string) => {
    setIsLoading(true);
    mutateVerifyOtp(otp);
  };

  const handleSetError = () => {
    setIsError(false);
  };

  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;

  const globalConfig = cmsGlobalConfig?.globalconfig;

  const numOfFailures = parseInt(localStorage.getItem('numOfOtpFailures') || '0', 10);

  useEffect(() => {
    if (!isErrorGenerateOtp) {
      return;
    }

    if (numOfFailures < (globalConfig?.otp?.maxFailureNumber || 0)) {
      localStorage.setItem('numOfOtpFailures', (numOfFailures + 1).toString());
    } else {
      const otpError = globalConfig?.otp?.adHocError || {};

      const redirect = () => {
        window.open(otpError?.buttonLink || '', '_self');
      };
      errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
        title: otpError?.title || '',
        message: otpError?.description || '',
        onClose: redirect,
        actionEvent: redirect,
        actionText: otpError?.buttonLabel || '',
        trackError: getTrackError(errorMock(`generate`, generateOtpError || undefined)),
      });
    }
  }, [isErrorGenerateOtp]);


  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem('numOfOtpFailures');
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  useEffect(() => {
    setIsDisabledBackButton(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isErrorFindcallerPostOtp) {
      setIsError(true);
      setIsLoading(false);
      setShowLoader(false);
      if (findcallerPostOtpError) {
        trackView({
          ...getEnergyTrackError(
            product,
            errorMock('FindCallerPostOtp', findcallerPostOtpError),
            'OTP'
          ),
          page_error_code: findcallerPostOtpError.status || '',
        });
      }
    } else if (isSuccessFindcallerPostOtp) {
      setIsLoading(false);
      setFindCaller({
        ...findcallerPostOtpData,
        isVerified: true,
        verifyOtp: dataVerifyOtp?.id || '',
      });
      trackView({
        event_name: 'checkout_step1.4',
        event_label_track: 'energy loader',
        visitor_id_asset_active: getEncryptedMsisdn(findcallerPostOtpData),
      });
      resetData(queryClient, ['findCaller']);
      setTimeout(() => {
        handleOnFinish?.();
      }, 1000);
    }
  }, [isErrorFindcallerPostOtp, isSuccessFindcallerPostOtp]);

  useEffect(() => {
    if (isErrorVerifyOtp) {
      setIsError(true);
      setIsLoading(false);
      if (verifyOtpErrorData) {
        trackView({
          ...getEnergyTrackError(product, errorMock('VerifyOtp', verifyOtpErrorData), 'OTP'),
          page_error_code: verifyOtpErrorData.status || '',
        });
      }
    } else if (isSuccessVerifyOtp) {
      setShowLoader(true);
      refetchFindcallerPostOtp();
    }
  }, [isErrorVerifyOtp, isSuccessVerifyOtp]);

  useEffect(() => {
    if (findCaller.selectedContactMedium?.field)
      handleSendOtp(
        findCaller.selectedContactMedium.field,
        findCaller.selectedContactMedium.fieldType
      );
  }, []);

  return (
    <>
      <TaggingOtpCard />
      <Otp
        product={product as IProduct}
        contactMedium={findCaller.selectedContactMedium}
        isError={isError}
        isLoading={isLoading}
        reSendOtp={handleSendOtp}
        onVerify={handleVerify}
        setIsError={handleSetError}
        showLoaderCard={showLoader}
        configOtp={organizeOtp(
          dataFromCms?.personalinfomobile,
          findCaller?.selectedContactMedium?.fieldType === 'msisdn',
          findCaller?.selectedContactMedium
        )}
        isTimerActive={isTimerActive}
      />
    </>
  );
};

export default OtpCard;

